import React, { useState } from 'react';
import { Badge, Card, Flex, Tabs, Text } from '@radix-ui/themes';
import { LightningBoltIcon, PersonIcon } from '@radix-ui/react-icons';
import { useSmartHubFeed } from './useSmartHubFeed';
import TaskBar from './TaskBar';
import AckDrawer from './AckDrawer';
import AiInsightsBand from './ai/AiInsightsBand';
import type { SmartHubItem, SmartHubSubmissionGroup } from './types';

interface SmartHubPanelProps {
  organizationId?: string;
  // PHA-509: when set, the feed is filtered to this patient and the panel
  // shows a "patient context" header so the clinician knows the rail is
  // scoped to the chart they're viewing.
  patientId?: string;
  patientName?: string;
  // Stretch the card to the column's height (the patient-list rail). Off in
  // the chart's sticky, scrolling rail: there a 100% height squeezes the card
  // to the rail's max-height and its overflow:hidden inner clips the feed, so
  // the rail has nothing left to scroll.
  fillHeight?: boolean;
}

const SmartHubPanel: React.FC<SmartHubPanelProps> = ({
  organizationId,
  patientId,
  patientName,
  fillHeight = true,
}) => {
  const {
    taskBar,
    billing,
    billingDenied,
    enabledPrograms,
    loading,
    lastUpdated,
    error,
    refetchTaskBar,
  } = useSmartHubFeed(organizationId, patientId);
  // PHA-479: target is either a single SmartHub row OR a submission group
  // (when the user clicks "Acknowledge all" inside Recent patient answers).
  const [target, setTarget] = useState<SmartHubItem | SmartHubSubmissionGroup | null>(
    null,
  );

  return (
    <>
      <Card className="shadow-sm" style={fillHeight ? { height: '100%' } : { flexShrink: 0 }}>
        <Tabs.Root defaultValue="smart_hub">
          <Tabs.List className="border-b border-slate-200">
            <Tabs.Trigger value="smart_hub" className="flex items-center gap-2 px-4 py-3">
              <LightningBoltIcon width="16" height="16" />
              Smart Hub
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="smart_hub" className="p-6">
            <Flex direction="column" gap="4">
              {patientId && (
                <Flex align="center" gap="2" wrap="wrap">
                  <Badge color="blue" variant="soft" size="2">
                    <PersonIcon width="12" height="12" />
                    Patient context
                  </Badge>
                  {patientName && (
                    <Text size="2" weight="medium" className="text-slate-700">
                      {patientName}
                    </Text>
                  )}
                </Flex>
              )}
              {lastUpdated && (
                <Text size="1" color="gray">
                  Updated{' '}
                  {lastUpdated.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}

              {error && (
                <Text size="1" color="red">
                  {error}
                </Text>
              )}

              {/* PHA-567 / PHA-574: AI Insights band sits ABOVE TaskBar.
                  Stacking rule: the backend overlay sets `live_signal` on
                  any AI insight whose patient also appears in today's
                  live acute buckets, bumping that card to the top of the
                  band with a "● LIVE" pip and risk_tier=critical override.
                  TaskBar still renders out_of_bounds + hospitalizations
                  first internally — together those guarantee live acute
                  reads as the top item regardless of the nightly score. */}
              <AiInsightsBand organizationId={organizationId} patientId={patientId} />

              <TaskBar
                feed={taskBar}
                billing={billing}
                billingDenied={billingDenied}
                enabledPrograms={enabledPrograms}
                loading={loading}
                onSelect={setTarget}
                onSelectSubmission={setTarget}
              />
            </Flex>
          </Tabs.Content>
        </Tabs.Root>
      </Card>

      <AckDrawer
        target={target}
        onClose={() => setTarget(null)}
        onAcknowledged={async () => {
          await refetchTaskBar();
        }}
      />
    </>
  );
};

export default SmartHubPanel;
